import { useEffect } from 'react';
import { FiCheckCircle, FiX, FiAlertCircle, FiInfo } from 'react-icons/fi';

/**
 * Modal de confirmación reutilizable
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {string} props.title - Título del modal
 * @param {string} props.message - Mensaje principal
 * @param {string} props.type - Tipo: 'success', 'error', 'warning', 'info'
 * @param {string} props.buttonText - Texto del botón (default: "Aceptar")
 */
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  title = 'Confirmación', 
  message = '', 
  type = 'success',
  buttonText = 'Aceptar'
}) => {
  // Cerrar con tecla ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Configuración de colores e iconos según el tipo
  const config = {
    success: {
      icon: <FiCheckCircle className="w-16 h-16" />,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
    },
    error: {
      icon: <FiAlertCircle className="w-16 h-16" />,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    },
    warning: {
      icon: <FiAlertCircle className="w-16 h-16" />,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      buttonColor: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
    },
    info: {
      icon: <FiInfo className="w-16 h-16" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      buttonColor: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
    }
  };

  const currentConfig = config[type] || config.success;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con botón de cerrar */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Contenido */}
        <div className="px-8 pb-8 pt-4 text-center">
          {/* Icono animado */}
          <div className={`flex justify-center mb-6 animate-bounce-slow ${currentConfig.color}`}>
            {currentConfig.icon}
          </div>

          {/* Título */}
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {title}
          </h3>

          {/* Mensaje */}
          <div className={`${currentConfig.bgColor} ${currentConfig.borderColor} border rounded-lg p-4 mb-6`}>
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">
              {message}
            </p>
          </div>

          {/* Botón de acción */}
          <button
            onClick={onClose}
            className={`w-full ${currentConfig.buttonColor} text-white py-3 px-6 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            {buttonText}
          </button>
        </div>
      </div>

      {/* Estilos de animación */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        .animate-bounce-slow {
          animation: bounceSlow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ConfirmationModal;



